import { ProductSegment, ProductSegmentReq } from "../models";
import * as schemas from "./schemas";
import { getToken } from "~/apps/shared/services/token";
import * as net from "~/lib/net";
import { usePagedQuery } from "~/common/hooks/usePagedQuery";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export const getProductGroups = async ({
  offset,
  limit,
}: {
  offset: number;
  limit: number;
}): Promise<ProductSegment[]> => {
  const url = net.buildUrl(`/api/v2/productgroups/`, { offset, limit });
  const data = await net.apiGet(url, getToken());
  return schemas.ProductSegmentListSchema.load(data);
};

export const getProductGroupById = async (id: string): Promise<ProductSegment> => {
  const data = await net.apiGet(`/api/v2/productgroup/${id}`, getToken());
  return schemas.ProductSegmentSchema.load(data);
};

export const createProductGroup = async (req: ProductSegmentReq) => {
  const payload = schemas.ProductSegmentReqSchema.save(req);
  const data = await net.apiPost("/api/v2/productgroup/", getToken(), payload);
  return schemas.ProductSegmentSchema.load(data);
};

export const editProductGroup = async ({id, ...rest}: ProductSegmentReq) => {
  const payload = schemas.ProductSegmentReqSchema.save(rest);
  const data = await net.apiPut(`/api/v2/productgroup/${id}`, getToken(), payload);
  return schemas.ProductSegmentSchema.load(data);
};

export const deleteProductGroup = async (id: string) => {
  return await net.apiDelete(`/api/v2/productgroup/${id}`, getToken());
};


// Queries

export const useProductGroups = () => {
  return usePagedQuery(["products-groups", "list"], (offset, limit) => getProductGroups({offset, limit}));
};

export const useProductGroupById = (id: string) => {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: ["products-groups", id],
    queryFn: () => getProductGroupById(id),
    enabled: !!id,
  });
};

export const useCreateProductGroup = () => {
  const queryClient = useQueryClient();
  return useMutation<ProductSegment, { message?: string; errorMessage?: string }, ProductSegmentReq>({
    mutationFn: createProductGroup,
    onSuccess: () => {
      queryClient.invalidateQueries(["products-groups"]);
    },
  });
};

export const useEditProductGroup = () => {
  const queryClient = useQueryClient();
  return useMutation<ProductSegment, { message?: string; errorMessage?: string }, ProductSegmentReq>({
    mutationFn: editProductGroup,
    onSuccess: () => {
      queryClient.invalidateQueries(["products-groups"]);
    },
  });
};

export const useDeleteProductGroup = () => {
  const queryClient = useQueryClient();
  return useMutation<undefined, { message?: string; errorMessage?: string }, string>({
    mutationFn: deleteProductGroup,
    onSuccess: () => {
      queryClient.invalidateQueries(["products-groups"]);
    },
  });
};
